/**
 * Language Detection Utility
 * 
 * Two-phase language detection:
 * Phase 1 (sync): URL param > stored preference > browser languages > "en"
 * Phase 2 (async): Cloudflare geo-detection on first visit only
 */

import { STORAGE_KEYS } from '@/utils/constants';
import type { Language } from '@/types/common';

const SPANISH_SPEAKING_COUNTRIES = new Set([
  "CO", "MX", "ES", "AR", "PE", "CL", "EC", "GT", "CU", "BO",
  "DO", "HN", "PY", "SV", "NI", "CR", "PA", "UY", "VE", "GQ"
]);

/**
 * Phase 1: Synchronous detection — runs before first render.
 * Priority: URL param > stored preference > browser languages > "en"
 */
export const detectInitialLanguage = (): Language => {
  // 1. URL parameter override (for ad campaigns: ?lang=es)
  try {
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param === "es" || param === "en") return param;
  } catch {}

  // 2. Stored preference (returning user) — Zustand persist format
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.language) || "");
    if (stored?.state?.language === "en" || stored?.state?.language === "es") {
      return stored.state.language;
    }
  } catch {}

  // 3. Browser language hint
  const langs = navigator.languages || [navigator.language || ""];
  if (langs.some(l => l?.toLowerCase().startsWith("es"))) return "es";

  // 4. Default
  return "en";
};

/**
 * Returns true if this is a first-time visit (no stored language preference).
 */
export const isFirstVisit = (): boolean => {
  try {
    return !localStorage.getItem(STORAGE_KEYS.language);
  } catch {
    return true;
  }
};

/**
 * Phase 2: Async geo detection — runs after mount, only on first visit.
 * Fetches Cloudflare's /cdn-cgi/trace to get country code.
 * Returns "es" or "en" based on country, or null if detection fails.
 */
export const detectLanguageFromGeo = async (): Promise<Language | null> => {
  try {
    const response = await fetch("/cdn-cgi/trace");
    if (!response.ok) return null;
    const text = await response.text();
    const match = text.match(/loc=(\w{2})/);
    if (!match) return null;
    const country = match[1].toUpperCase();
    return SPANISH_SPEAKING_COUNTRIES.has(country) ? "es" : "en";
  } catch {
    return null;
  }
};

/**
 * Remove ?lang= parameter from URL after consumption.
 */
export const cleanLangParam = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("lang")) {
      params.delete("lang");
      const clean = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState(null, "", clean);
    }
  } catch {}
};
